import parsePhoneNumberFromString from 'libphonenumber-js';
import type { IntlShape } from 'react-intl/src/types';
import type { InferType } from 'yup';
import { bool, object, string } from 'yup';

import { CountryCodeEnum } from '@/types/regionTypes';

import type { CountryCode, UserFragment } from '@/graphql/saleor/api';
import { EMAIL_REGX, MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import {
  getPhonePrefixSelectOptByCode,
  getPhonePrefixSelectOptByValue,
} from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';
import type { PhonePrefix } from '@/views/DeliveryView/helpers';
import type { CountryOption, LanguageOption } from '@/views/SignUpView/helper';

export type CatalogDownloadSchema = Omit<
  InferType<ReturnType<typeof catalogDownloadSchema>>,
  'language' | 'country'
> & {
  country: CountryOption;
  language: LanguageOption;
  recaptchaResponse?: string;
};

export const catalogDownloadSchema = (
  formatMessage: IntlShape['formatMessage'],
  countryCode: CountryCode,
  user: Partial<UserFragment> | null,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const invalidPhoneMsg = formatMessage(formMessages.invalidPhone);
  const isJapan = countryCode === CountryCodeEnum.JP;

  let _salutation = '';
  let _phonePrefix = getPhonePrefixSelectOptByCode(countryCode);
  let _phone = '';
  if (user && user.metadata) {
    const userTitle =
      getMetaValue(user.metadata, ACCOUNT_METADATA.personalTitle) ?? '';
    _salutation = isJapan && userTitle === 'Mrs' ? 'Ms' : userTitle;
    _phonePrefix = getPhonePrefixSelectOptByValue(
      getMetaValue(user.metadata, ACCOUNT_METADATA.phonePrefix) ?? '',
    );
    _phone = getMetaValue(user.metadata, ACCOUNT_METADATA.phone) ?? '';
  }

  return object({
    salutation: getTitleSchema(formatMessage, countryCode, _salutation),
    firstName: getNameSchema(formatMessage, 'first', user?.firstName),
    lastName: getNameSchema(formatMessage, 'last', user?.lastName),
    ...(isJapan && {
      firstNameKanji: getNameSchema(
        formatMessage,
        'firstKanji',
        user?.metadata
          ? getMetaValue(user.metadata, ACCOUNT_METADATA.first_name_kanji ?? '')
          : '',
      ),
      lastNameKanji: getNameSchema(
        formatMessage,
        'lastKanji',
        user?.metadata
          ? getMetaValue(user.metadata, ACCOUNT_METADATA.last_name_kanji ?? '')
          : '',
      ),
    }),
    country: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).noUnknown(false),
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(user?.email ?? ''),
    phonePrefix: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).default(_phonePrefix),
    phone: string()
      .required(requiredMsg)
      .max(
        MAX_CHAR_PHONE,
        formatMessage(formMessages.maxChars, {
          max: MAX_CHAR_PHONE,
        }),
      )
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix,
        then: (schema) =>
          schema.test('is-valid-phone', invalidPhoneMsg, function (value) {
            const { phonePrefix } = this.parent;
            const fullPhoneNumber = `${phonePrefix.value}${value}`;
            const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
            return phoneNumber ? phoneNumber.isValid() : false;
          }),
      })
      .default(_phone),
    language: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    }).noUnknown(false),
    catalogDownloadConsent: bool().default(false),
    catalogDownloadPrivacyConfirm: bool()
      .default(false)
      .oneOf([true], requiredMsg),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};
